import React, { Component } from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import { Stack, Paper } from '@mui/material'

import { saveAs } from 'file-saver'
import writeXlsxFile from 'write-excel-file'
import { format } from 'date-fns'

import ReportsToolbar from './ReportsToolbar'
import ReportsGrid from './ReportsGrid'
import { buildRows } from './reports.rows'
import { formatDate, formatReportTypes, normalizeId } from './reports.utils'

class Reports extends Component {
    componentDidMount() {
        this.props.getReportCases()
        this.props.fetchReportCaseAssignees()
    }

    handleDelete = async (row) => {
        if (!row?._id) return
        if (!window.confirm(`Delete report case for "${row.companyName || 'Untitled'}"?`)) return
        await this.props.deleteReportCase(row._id)
    }

    processRowUpdate = async (newRow, oldRow) => {
        if (!newRow?._id) return oldRow

        const patch = {}

        if (newRow.status !== oldRow.status) patch.status = newRow.status
        if (newRow.nextFollowUpDate !== oldRow.nextFollowUpDate) {
            patch.nextFollowUpDate = newRow.nextFollowUpDate || null
        }
        if (newRow.companySecretary !== oldRow.companySecretary) {
            patch.companySecretary = newRow.companySecretary || ''
        }
        if (newRow.taxAgent !== oldRow.taxAgent) {
            patch.taxAgent = newRow.taxAgent || ''
        }

        if (!Object.keys(patch).length) return oldRow

        await this.props.updateReportCase(newRow._id, patch)
        return { ...newRow, ...patch }
    }

    handleExportExcel = async () => {
        const cases = extractReportCases(this.props.reportCases)

        const sheets = ['Report Cases', 'Comments']
        const dataCases = buildCasesSheet(cases)
        const dataComments = buildCommentsSheet(cases)

        const blob = await writeXlsxFile(
            [dataCases, dataComments],
            { sheets, stickyRowsCount: 1 }
        )

        const formattedDate = format(new Date(), 'd MMMM yyyy HH-mm')
        saveAs(blob, `FMD-Workflow-${formattedDate}.xlsx`)
    }

    render() {
        const rows = buildRows(this.props.reportCases, this.props.reportCaseAssignees)

        return (
            // <Container maxWidth="xl" sx={{ py: 1 }}>
                <Stack spacing={2} px={4}>
                    <ReportsToolbar
                        onCreate={() => this.props.onChangeItem('new-report-case')}
                        onExport={this.handleExportExcel}
                        canExportReports={this.props.canExportReports}
                    />

                    <Paper>
                        <ReportsGrid
                            rows={rows}
                            loading={this.props.reportCasesLoading}
                            onDelete={this.props.canDeleteReports ? this.handleDelete : null}
                            onNavigate={(row) =>
                                this.props.onChangeItem('view-report-case', row)
                            }
                            processRowUpdate={this.processRowUpdate}
                            canDeleteReports={this.props.canDeleteReports}
                        />
                    </Paper>
                </Stack>
            // </Container>
        )
    }
}

function extractReportCases(reportCases) {
    if (!reportCases) return []
    if (Array.isArray(reportCases?.items)) return reportCases.items
    if (Array.isArray(reportCases)) return reportCases
    if (Array.isArray(reportCases?.data)) return reportCases.data
    if (Array.isArray(reportCases?.reportCases)) return reportCases.reportCases
    if (typeof reportCases === 'object') return Object.values(reportCases)
    return []
}

function cell(value, bold) {
    const v = value == null ? '' : value
    return bold ? { value: v, fontWeight: 'bold' } : { value: v }
}

function buildCasesSheet(cases) {
    const header = [
        cell('Company Name', true),
        cell('PIC (Name)', true),
        cell('Status', true),
        cell('FYE', true),
        cell('Report Types', true),
        cell('Email Received', true),
        cell('Company Secretary', true),
        cell('Tax Agent', true),
        cell('Due Date', true),
        cell('Next Follow-up Date', true),
        cell('Is Urgent', true),
        cell('Sent To Client Date', true),
        cell('Delivery Method', true),
        cell('Courier', true),
        cell('Status – Doc to Return', true),
        cell('Comment for Docs to Return', true),
        cell('Status – Accounts Records to Return', true),
        cell('Comment for Accounts Records to Return', true),
        cell('Signed Documents Received Date', true),
        cell('Total Outstanding', true),
        cell('Created At', true),
        cell('Updated At', true),
    ]

    const rows = (Array.isArray(cases) ? cases : []).map(r => {
        const picObj = r.personInCharge && typeof r.personInCharge === 'object' ? r.personInCharge : null

        return [
            cell(r.companyName),
            cell(picObj?.name || ''),
            cell(r.status || ''),
            cell(r.fye || ''),
            cell(formatReportTypes(r.reportTypes, r.xbrlService)),
            cell(formatDate(r.emailReceivedOn)),
            cell(r.companySecretary || ''),
            cell(r.taxAgent || ''),
            cell(formatDate(r.dueDate)),
            cell(formatDate(r.nextFollowUpDate)),
            cell(formatDate(r.isUrgent)),
            cell(formatDate(r.sentToClientDate)),
            cell(r.deliveryMethod || ''),
            cell(r.courierCompany || ''),
            cell(r.deliverySigningStatus || ''),
            cell(r.deliverySigningRemarks || ''),
            cell(r.documentsReturnStatus || ''),
            cell(r.incompleteDocsRemark || ''),
            cell(formatDate(r.signedReceivedDate)),
            cell(r.totalOutstanding == null ? '' : Number(r.totalOutstanding)),
            cell(formatDate(r.createdAt)),
            cell(formatDate(r.updatedAt)),
        ]
    })

    return [header, ...rows]
}

function buildCommentsSheet(cases) {
    const header = [
        cell('Case ID', true),
        cell('Company', true),
        cell('User ID', true),
        cell('Name', true),
        cell('Message', true),
        cell('Created At', true)
    ]

    const out = [header]

    ;(Array.isArray(cases) ? cases : []).forEach(r => {
        const cs = Array.isArray(r.comments) ? r.comments : []
        cs.forEach(c => {
            out.push([
                cell(r._id),
                cell(r.companyName),
                cell(normalizeId(c?.userId)),
                cell(c?.name || ''),
                cell(c?.message || ''),
                cell(formatDate(c?.createdAt))
            ])
        })
    })

    return out
}

function mapStateToProps({ reportCases, reportCaseAssignees, reportCasesLoading }) {
    return { reportCases, reportCaseAssignees, reportCasesLoading }
}

export default connect(mapStateToProps, actions)(Reports)
